import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Trash2, RefreshCw, X, Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useUserData } from "@/hooks/useUserData";
import type { MovieData } from "@/components/SwipeCard";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import TmdbAttribution from "@/components/TmdbAttribution";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type SentimentKey = "loved" | "pretty_good" | "ok" | "not_great" | "hated";

interface HistoryItem extends MovieData { seenRating?: string; picked?: boolean; media_type?: string; }

const WatchHistory = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getWatchHistory, getFavorites, removeFromWatchHistory, addToWatchHistory, getCurrentlyWatching, addCurrentlyWatching, removeCurrentlyWatching } = useUserData();
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [favorites, setFavorites] = useState<any[]>([]);
  const [mediaFilter, setMediaFilter] = useState<"movie" | "tv">("movie");
  const [loading, setLoading] = useState(true);
  const [selectedMovie, setSelectedMovie] = useState<HistoryItem | null>(null);
  const [changingRating, setChangingRating] = useState(false);

  // Currently watching state
  const [cwShows, setCwShows] = useState<any[]>([]);
  const [showAddCw, setShowAddCw] = useState(false);
  const [cwSearch, setCwSearch] = useState("");
  const [cwResults, setCwResults] = useState<any[]>([]);
  const [cwSearching, setCwSearching] = useState(false);
  const [selectedCwShow, setSelectedCwShow] = useState<any | null>(null);
  const [cwRemoveRating, setCwRemoveRating] = useState(false);

  const sentimentSections: { key: SentimentKey; label: string; emoji: string }[] = [
    { key: "loved", label: t("watchHistory.lovedIt"), emoji: "😍" },
    { key: "pretty_good", label: t("watchHistory.prettyGood"), emoji: "😊" },
    { key: "ok", label: t("watchHistory.ok"), emoji: "😐" },
    { key: "not_great", label: t("watchHistory.notGreat"), emoji: "😕" },
    { key: "hated", label: t("watchHistory.hatedIt"), emoji: "😡" },
  ];

  const reload = () => {
    Promise.all([getWatchHistory(), getFavorites(), getCurrentlyWatching()])
      .then(([history, favs, cw]) => { setItems(history); setFavorites(favs); setCwShows(cw); })
      .catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [getWatchHistory, getFavorites, getCurrentlyWatching]);

  const handleRemove = async (movie: HistoryItem) => { await removeFromWatchHistory(movie.id); setSelectedMovie(null); reload(); };
  const handleChangeRating = async (movie: HistoryItem, newRating: SentimentKey) => { await addToWatchHistory(movie, newRating, movie.picked, movie.media_type || "movie"); setSelectedMovie(null); setChangingRating(false); reload(); };

  // Currently watching search
  const searchCwShows = useCallback(async (query: string) => {
    if (query.length < 2) { setCwResults([]); return; }
    setCwSearching(true);
    try {
      const lang = localStorage.getItem("ayeris_language") || "en";
      const region = localStorage.getItem("ayeris_region") || "GB";
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      // Read age flags from profile/localStorage
      const storedProfile = localStorage.getItem("ayeris_profile");
      let is15Plus = true, is18Plus = true;
      if (storedProfile) {
        try { const p = JSON.parse(storedProfile); is15Plus = p.is_15_plus ?? true; is18Plus = p.is_18_plus ?? true; } catch {}
      }

      const params = new URLSearchParams({
        type: "tv", query, region, language: lang, page: "1",
        is_15_plus: String(is15Plus),
        is_18_plus: String(is18Plus),
      });
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/tmdb-popular?${params}`,
        { headers: { 'Authorization': `Bearer ${anonKey}`, 'apikey': anonKey } }
      );
      if (!response.ok) throw new Error("Search failed");
      const data = await response.json();
      // No provider filtering here — the search is for "currently watching" which
      // may include shows from any service. Results are already age-filtered server-side.
      setCwResults((data?.results || []).slice(0, 10));
    } catch (e) {
      console.error(e);
    } finally {
      setCwSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { if (cwSearch) searchCwShows(cwSearch); else setCwResults([]); }, 400);
    return () => clearTimeout(timer);
  }, [cwSearch, searchCwShows]);

  const handleAddCw = async (show: any) => {
    await addCurrentlyWatching({
      id: show.id,
      title: show.title || show.name,
      poster: show.poster || (show.poster_path ? `https://image.tmdb.org/t/p/w500${show.poster_path}` : ""),
      year: show.year || (show.first_air_date ? new Date(show.first_air_date).getFullYear() : 0),
      media_type: "tv",
    });
    toast.success(t("discover.addedToCurrentlyWatching"));
    setCwSearch("");
    setCwResults([]);
    setShowAddCw(false);
    reload();
  };

  const handleRemoveCw = async (show: any) => {
    setSelectedCwShow(show);
    setCwRemoveRating(true);
  };

  const confirmRemoveCw = async (rating?: SentimentKey) => {
    if (!selectedCwShow) return;
    await removeCurrentlyWatching(selectedCwShow.id);
    if (rating) {
      const movieData: MovieData = {
        id: selectedCwShow.id,
        title: selectedCwShow.title,
        synopsis: "",
        poster: selectedCwShow.poster || "",
        year: selectedCwShow.year || 0,
        runtime: 0,
        rating: 0,
        genres: [],
      };
      await addToWatchHistory(movieData, rating, false, "tv");
    }
    toast.info(t("discover.removedFromCurrentlyWatching"));
    setSelectedCwShow(null);
    setCwRemoveRating(false);
    reload();
  };

  const allLoved = (() => {
    const historyLoved = items.filter((i) => i.seenRating === "loved" && (i.media_type || "movie") === mediaFilter);
    const favItems: HistoryItem[] = favorites.filter((f) => (f.media_type || "movie") === mediaFilter).map((f) => ({ id: f.id || f.tmdb_id, title: f.title, synopsis: "", poster: f.poster || "", year: f.year || 0, runtime: 0, rating: 0, genres: [], seenRating: "loved", media_type: f.media_type || "movie" }));
    const seenIds = new Set(historyLoved.map((h) => h.id));
    return [...historyLoved, ...favItems.filter((f) => !seenIds.has(f.id))];
  })();

  const grouped: Record<SentimentKey, HistoryItem[]> = {
    loved: allLoved,
    pretty_good: items.filter((i) => i.seenRating === "pretty_good" && (i.media_type || "movie") === mediaFilter),
    ok: items.filter((i) => (i.seenRating === "ok" || i.seenRating === "meh") && (i.media_type || "movie") === mediaFilter),
    not_great: items.filter((i) => i.seenRating === "not_great" && (i.media_type || "movie") === mediaFilter),
    hated: items.filter((i) => i.seenRating === "hated" && (i.media_type || "movie") === mediaFilter),
  };

  return (
    <div className="h-[100dvh] bg-background flex flex-col pb-14" style={{ paddingBottom: "calc(3.5rem + env(safe-area-inset-bottom, 0px))" }}>
      <div className="flex-shrink-0 px-4 pt-6 pb-4">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
          <button onClick={() => navigate("/home")} className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center active:scale-95 transition-transform"><ChevronLeft className="w-5 h-5 text-foreground" /></button>
          <div>
            <h1 className="font-display text-xl font-bold text-foreground">{t("watchHistory.title")}</h1>
            <p className="text-xs text-muted-foreground">{t("watchHistory.subtitle")}</p>
          </div>
        </motion.div>
      </div>

      <div className="flex-shrink-0 px-4 mb-4">
        <div className="flex gap-3">
          {([{ type: "movie" as const, emoji: "🎬", label: t("common.movies") }, { type: "tv" as const, emoji: "📺", label: t("common.tvShows") }]).map((opt) => (
            <button key={opt.type} onClick={() => setMediaFilter(opt.type)} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all active:scale-95 ${mediaFilter === opt.type ? "border-primary bg-primary/10" : "border-border bg-secondary hover:border-muted-foreground/30"}`}>
              <span className="text-xl">{opt.emoji}</span>
              <span className={`text-sm font-medium ${mediaFilter === opt.type ? "text-primary" : "text-muted-foreground"}`}>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="space-y-5 pb-6">
            {/* Currently Watching — TV only */}
            {mediaFilter === "tv" && (
              <div>
                <div className="mx-4 mb-3 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary/15 to-transparent border border-primary/20 flex items-center justify-between">
                  <h3 className="font-display text-base font-semibold text-foreground">📺 {t("watchHistory.currentlyWatching")}<span className="ml-2 text-xs text-muted-foreground font-normal">({cwShows.length})</span></h3>
                  <button
                    onClick={() => setShowAddCw(true)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-primary/15 hover:bg-primary/25 text-primary text-xs font-medium transition-colors active:scale-95"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {t("watchHistory.addShow")}
                  </button>
                </div>
                {cwShows.length === 0 ? (
                  <div className="px-4"><div className="rounded-xl bg-secondary/50 border border-border p-4 text-center"><p className="text-sm text-muted-foreground">{t("watchHistory.currentlyWatchingEmpty")}</p></div></div>
                ) : (
                  <CurrentlyWatchingRow items={cwShows} onRemove={handleRemoveCw} />
                )}
              </div>
            )}

            {sentimentSections.map(({ key, label, emoji }) => (
              <SentimentSection key={key} sentimentKey={key} label={label} emoji={emoji} items={grouped[key]} onSelect={setSelectedMovie} nothingLabel={t("watchHistory.nothingHere")} />
            ))}
            <TmdbAttribution className="pt-4 pb-2" />
            <p className="text-[10px] text-muted-foreground/60 text-center mb-4">© 2026 Ayeris.tv — All rights reserved.</p>
          </div>
        )}
      </div>

      {/* Movie options dialog */}
      <Dialog open={!!selectedMovie} onOpenChange={(open) => { if (!open) { setSelectedMovie(null); setChangingRating(false); } }}>
        <DialogContent className="max-w-[320px] rounded-2xl p-0 overflow-hidden border-border">
          <DialogTitle className="sr-only">{t("watchHistory.movieOptions")}</DialogTitle>
          {selectedMovie && (
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-3 w-full p-4 pb-3">
                <div className="w-14 h-20 rounded-lg overflow-hidden border border-border flex-shrink-0"><img src={selectedMovie.poster} alt={selectedMovie.title} className="w-full h-full object-cover" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate">{selectedMovie.title}</p>
                  <p className="text-xs text-muted-foreground">{selectedMovie.year}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("watchHistory.currentRating")} {sentimentSections.find(s => s.key === selectedMovie.seenRating)?.emoji} {sentimentSections.find(s => s.key === selectedMovie.seenRating)?.label}
                  </p>
                </div>
              </div>
              <div className="w-full border-t border-border" />
              {!changingRating ? (
                <div className="w-full p-3 space-y-2">
                  <button onClick={() => setChangingRating(true)} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-secondary hover:bg-secondary/80 transition-colors active:scale-[0.98]">
                    <RefreshCw className="w-4 h-4 text-primary" /><span className="text-sm font-medium text-foreground">{t("watchHistory.changeRating")}</span>
                  </button>
                  <button onClick={() => handleRemove(selectedMovie)} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-destructive/10 hover:bg-destructive/20 transition-colors active:scale-[0.98]">
                    <Trash2 className="w-4 h-4 text-destructive" /><span className="text-sm font-medium text-destructive">{t("watchHistory.removeFromHistory")}</span>
                  </button>
                </div>
              ) : (
                <div className="w-full p-3">
                  <p className="text-xs text-muted-foreground mb-2 px-1">{t("watchHistory.chooseNewRating")}</p>
                  <div className="grid grid-cols-5 gap-1.5">
                    {[...sentimentSections].reverse().map(({ key, label, emoji }) => (
                      <button key={key} onClick={() => handleChangeRating(selectedMovie, key)} className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all active:scale-90 ${selectedMovie.seenRating === key ? "bg-primary/15 ring-2 ring-primary" : "bg-secondary hover:bg-secondary/80"}`}>
                        <span className="text-xl">{emoji}</span>
                        <span className="text-[9px] text-muted-foreground leading-tight text-center">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Currently Watching search dialog */}
      <Dialog open={showAddCw} onOpenChange={(open) => { if (!open) { setShowAddCw(false); setCwSearch(""); setCwResults([]); } }}>
        <DialogContent className="max-w-[360px] rounded-2xl p-4 border-border">
          <DialogTitle className="font-display text-lg font-bold text-foreground mb-3">{t("watchHistory.addShow")}</DialogTitle>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={cwSearch}
              onChange={(e) => setCwSearch(e.target.value)}
              placeholder={t("watchHistory.searchShows")}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-secondary border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              autoFocus
            />
          </div>
          <div className="max-h-[300px] overflow-y-auto space-y-1.5">
            {cwSearching && <div className="flex justify-center py-4"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}
            {cwResults.map((show) => {
              const alreadyAdded = cwShows.some((cw) => cw.id === show.id);
              return (
                <button
                  key={show.id}
                  onClick={() => !alreadyAdded && handleAddCw(show)}
                  disabled={alreadyAdded}
                  className={`w-full flex items-center gap-3 p-2 rounded-xl transition-colors active:scale-[0.98] ${alreadyAdded ? "opacity-50 cursor-not-allowed bg-secondary/50" : "bg-secondary hover:bg-secondary/80"}`}
                >
                  <div className="w-10 h-14 rounded-lg overflow-hidden border border-border flex-shrink-0">
                    {show.poster ? <img src={show.poster} alt={show.title || show.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-muted" />}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-foreground truncate">{show.title || show.name}</p>
                    <p className="text-xs text-muted-foreground">{show.year || (show.first_air_date ? new Date(show.first_air_date).getFullYear() : "")}</p>
                  </div>
                  {alreadyAdded ? (
                    <span className="text-xs text-muted-foreground">✓</span>
                  ) : (
                    <Plus className="w-4 h-4 text-primary flex-shrink-0" />
                  )}
                </button>
              );
            })}
            {cwSearch.length >= 2 && !cwSearching && cwResults.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">{t("common.noResults")}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove from Currently Watching dialog (with optional rating) */}
      <Dialog open={cwRemoveRating} onOpenChange={(open) => { if (!open) { setCwRemoveRating(false); setSelectedCwShow(null); } }}>
        <DialogContent className="max-w-[320px] rounded-2xl p-0 overflow-hidden border-border">
          <DialogTitle className="sr-only">{t("watchHistory.stopWatching")}</DialogTitle>
          {selectedCwShow && (
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-3 w-full p-4 pb-3">
                <div className="w-14 h-20 rounded-lg overflow-hidden border border-border flex-shrink-0">
                  {selectedCwShow.poster ? <img src={selectedCwShow.poster} alt={selectedCwShow.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-muted" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate">{selectedCwShow.title}</p>
                  <p className="text-xs text-muted-foreground">{selectedCwShow.year}</p>
                </div>
              </div>
              <div className="w-full border-t border-border" />
              <div className="w-full p-3">
                <p className="text-xs text-muted-foreground mb-3 px-1">{t("watchHistory.rateBeforeRemove")}</p>
                <div className="grid grid-cols-5 gap-1.5 mb-3">
                  {[...sentimentSections].reverse().map(({ key, label, emoji }) => (
                    <button key={key} onClick={() => confirmRemoveCw(key)} className="flex flex-col items-center gap-1 p-2 rounded-xl transition-all active:scale-90 bg-secondary hover:bg-secondary/80">
                      <span className="text-xl">{emoji}</span>
                      <span className="text-[9px] text-muted-foreground leading-tight text-center">{label}</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => confirmRemoveCw()} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-destructive/10 hover:bg-destructive/20 transition-colors active:scale-[0.98]">
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  <span className="text-sm font-medium text-destructive">{t("watchHistory.justRemove")}</span>
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Currently watching horizontal row
const CurrentlyWatchingRow = ({ items, onRemove }: { items: any[]; onRemove: (show: any) => void }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => { const el = scrollRef.current; if (!el) return; setCanScrollLeft(el.scrollLeft > 4); setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4); };
  useEffect(() => { checkScroll(); const el = scrollRef.current; if (el) el.addEventListener("scroll", checkScroll, { passive: true }); return () => el?.removeEventListener("scroll", checkScroll); }, [items]);
  const scroll = (dir: "left" | "right") => { const el = scrollRef.current; if (!el) return; el.scrollBy({ left: dir === "left" ? -el.clientWidth * 0.8 : el.clientWidth * 0.8, behavior: "smooth" }); };

  return (
    <div className="relative">
      {items.length > 4 && (
        <div className="absolute right-4 top-0 -translate-y-8 flex items-center gap-1 z-10">
          <button onClick={() => scroll("left")} disabled={!canScrollLeft} className={`w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 ${canScrollLeft ? "bg-background/50 hover:bg-background/80 text-foreground" : "opacity-0 pointer-events-none"}`}><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => scroll("right")} disabled={!canScrollRight} className={`w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 ${canScrollRight ? "bg-background/50 hover:bg-background/80 text-foreground" : "opacity-0 pointer-events-none"}`}><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
      <div ref={scrollRef} className="flex gap-3 overflow-x-auto px-4 pb-2" style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}>
        {items.map((show, i) => (
          <motion.div key={show.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="flex-shrink-0 w-[calc(25%-9px)] cursor-pointer group relative" onClick={() => onRemove(show)}>
            <div className="aspect-[2/3] rounded-lg overflow-hidden border-2 border-primary/30 mb-1.5 active:scale-95 transition-transform relative">
              <img src={show.poster} alt={show.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <X className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <p className="text-xs text-foreground truncate font-medium">{show.title}</p>
            <p className="text-[10px] text-muted-foreground">{show.year}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const sentimentBannerColor: Record<SentimentKey, string> = {
  loved: "from-sentiment-love/15 to-transparent border-sentiment-love/20",
  pretty_good: "from-sentiment-pretty-good/15 to-transparent border-sentiment-pretty-good/20",
  ok: "from-sentiment-ok/15 to-transparent border-sentiment-ok/20",
  not_great: "from-sentiment-not-great/15 to-transparent border-sentiment-not-great/20",
  hated: "from-sentiment-nah/15 to-transparent border-sentiment-nah/20",
};

const SentimentSection = ({ sentimentKey, label, emoji, items, onSelect, nothingLabel }: { sentimentKey: SentimentKey; label: string; emoji: string; items: HistoryItem[]; onSelect: (movie: HistoryItem) => void; nothingLabel: string; }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => { const el = scrollRef.current; if (!el) return; setCanScrollLeft(el.scrollLeft > 4); setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4); };
  useEffect(() => { checkScroll(); const el = scrollRef.current; if (el) el.addEventListener("scroll", checkScroll, { passive: true }); return () => el?.removeEventListener("scroll", checkScroll); }, [items]);
  const scroll = (dir: "left" | "right") => { const el = scrollRef.current; if (!el) return; el.scrollBy({ left: dir === "left" ? -el.clientWidth * 0.8 : el.clientWidth * 0.8, behavior: "smooth" }); };

  return (
    <div>
      <div className={`mx-4 mb-3 px-4 py-2.5 rounded-xl bg-gradient-to-r ${sentimentBannerColor[sentimentKey]} border flex items-center justify-between`}>
        <h3 className="font-display text-base font-semibold text-foreground">{emoji} {label}<span className="ml-2 text-xs text-muted-foreground font-normal">({items.length})</span></h3>
        {items.length > 0 && (
          <div className="flex items-center gap-1">
            <button onClick={() => scroll("left")} disabled={!canScrollLeft} className={`w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 ${canScrollLeft ? "bg-background/50 hover:bg-background/80 text-foreground" : "opacity-0 pointer-events-none"}`}><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => scroll("right")} disabled={!canScrollRight} className={`w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90 ${canScrollRight ? "bg-background/50 hover:bg-background/80 text-foreground" : "opacity-0 pointer-events-none"}`}><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <div className="px-4"><div className="rounded-xl bg-secondary/50 border border-border p-4 text-center"><p className="text-sm text-muted-foreground">{nothingLabel}</p></div></div>
      ) : (
        <div ref={scrollRef} className="flex gap-3 overflow-x-auto px-4 pb-2" style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}>
          {items.map((movie, i) => (
            <motion.div key={movie.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="flex-shrink-0 w-[calc(25%-9px)] cursor-pointer" onClick={() => onSelect(movie)}>
              <div className="aspect-[2/3] rounded-lg overflow-hidden border border-border mb-1.5 active:scale-95 transition-transform"><img src={movie.poster} alt={movie.title} className="w-full h-full object-cover" /></div>
              <p className="text-xs text-foreground truncate font-medium">{movie.title}</p>
              <p className="text-[10px] text-muted-foreground">{movie.year}</p>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WatchHistory;
